import { State } from 'src/replicant/State';
import * as Pet from 'src/replicant/pet';
import { PetState } from './pet.redux';
import StateObserver from 'src/StateObserver';
import * as Fate from '../../replicant/fate';
import * as Visit from 'src/replicant/visit';
import type { PetState as DisplayedPetState } from 'src/replicant/pet';

export function syncPetState(initialState: PetState, user: State): PetState {
  const now = StateObserver.now();
  const { getAttributeValues, getPetAge, getCurrentEvolution } = Pet.getters;

  const petState = { ...initialState };
  petState.species = getCurrentEvolution(user)?.species;
  petState.statusEffects = Fate.getters.getAllPermanentStatusEffects(user, now);
  petState.attributes = Object.assign(
    { ...petState.attributes },
    {
      name: user.pet.name,
      age: getPetAge(user, now),
      ...getAttributeValues(user, now),
    },
  );

  return petState;
}

/**
 * The pet state the appearance controller renders.
 * `Fate.getters.getVisiblePetState` answers `sleeping` for a pet in bed and
 * `evolving` for a pending evolution, neither of which has a status animation.
 * The controller drops both, since sleep is shown through the bed action and
 * the evolution plays as a launch sequence, and renders the attribute-driven
 * status behind them. Anything keyed on the fate state misses that animation.
 */
export function getDisplayedPetState(user: State): DisplayedPetState {
  const now = StateObserver.now();

  return getDisplayedPetStateFrom(
    Pet.getters.getAttributeValues(user, now),
    Fate.getters.getAllPermanentStatusEffects(user, now),
    Visit.getters.isVisiting(user),
  );
}

export function getDisplayedPetStateFrom(
  attributes: Parameters<typeof Pet.ruleset.getPetState>[0],
  statusEffects: Parameters<typeof Pet.ruleset.getPetState>[1],
  isVisiting: boolean,
): DisplayedPetState {
  return Pet.ruleset.getPetState(
    attributes,
    {
      ...statusEffects,
      // Sleep is shown through the bed action animation, never the status vfx.
      asleep: false,
      // A pending evolution plays as a sequence at launch, after which the
      // attribute-driven status (dirty, hungry, ...) is what gets rendered.
      evolving: false,
    },
    isVisiting,
  );
}
